import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { useFeatures } from '@/contexts/root-store-context'
import { type UserManagementViewModel } from '@/features/user-management/UserManagement/UserManagementViewModel'
import {
  UserManagementTable,
  UserManagementTableHeaderRow,
} from '@/features/user-management/components/UserManagementTable'
import { UserManagementTableControlsContainer } from '@/features/user-management/components/UserManagementTableControlsContainer'
import { UserManagementTableRowContainer } from '@/features/user-management/components/UserManagementTableRow/UserManagementTableRowContainer'

export interface UserManagementTableContainerProps {
  vm: UserManagementViewModel
}

export const UserManagementTableContainer = observer(
  function UserManagementTableContainer({
    vm,
  }: UserManagementTableContainerProps) {
    const navigate = useNavigate()
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    const visitUserDetailsLink = (user: SearchableUser) =>
      navigate(vm.getUserDetailsLinkForUser(user))

    return (
      <UserManagementTable
        totalUsers={vm.userSearch.totalUsers}
        isAllWorkspaces={
          vm.workspaceSelector.selectedWorkspace === Workspace.none
        }
        isShowingInactiveUsers={vm.userSearch.userInactiveFilter.value}
        onInactiveChange={vm.userSearch.userInactiveFilter.change}
        controls={<UserManagementTableControlsContainer vm={vm} />}
        showEmptyState={vm.shouldShowEmptyState}
        isSearchFilterApplied={vm.isSearchFilterApplied}
        corporateMode={shellInfo.corporateMode}
        onClearSearchFilter={vm.clearFilters}
        table={
          <VirtualTableContainer
            overscan={50}
            scroller={vm.userSearch.scrollProvider}
            data={vm.users}
            headerContent={() => <HeaderRowContainer vm={vm} />}
            itemContent={(_index, user) => (
              <UserManagementTableRowContainer
                key={user.publicId}
                vm={vm}
                user={user}
              />
            )}
            onPressItemRow={visitUserDetailsLink}
          />
        }
      />
    )
  },
)

interface HeaderRowContainerProps {
  vm: UserManagementViewModel
}

// VirtualTableContainer renders the header outside this container's render, so the
// header needs its own observer to track the selection.
const HeaderRowContainer = observer(function HeaderRowContainer({
  vm,
}: HeaderRowContainerProps) {
  return (
    <UserManagementTableHeaderRow
      columns={vm.columns.map((col) => ({
        id: col.id,
        header: col.header,
        sortable: col.sortable,
        visible: col.visible,
      }))}
      selectionState={vm.selectedState}
      onToggleAll={vm.toggleAll}
    />
  )
})
